import type { ChatUpdate } from "./ChatBackendInterface";

/** The chat-side contract of the `create_editor_document` MCP tool (#11548
 * round 14): the model opens a live document the user edits beside the chat.
 * The tool result carries the created workspace id; the chat surface watches
 * tool results for it and opens the editor side panel. */

export const EDITOR_DOCUMENT_TOOL_NAME =
  "mcp__finpilot__create_editor_document";

/** What the chat surface should do when a document/artifact tool result
 * lands (#11548 r23): open the editor panel, refresh its content after a
 * model write, or open a file artifact in the viewer. */
export type EditorDocumentAction =
  // The artifact id when the result names one (open_artifact always
  // does): the pane needs it for its comments and version history.
  | {
      kind: "open";
      workspaceId: string;
      title: string | null;
      artifactId: string | null;
    }
  | { kind: "refresh"; workspaceId: string }
  | {
      kind: "open_file";
      artifactId: string;
      title: string | null;
      filename: string | null;
    }
  // A successful update_artifact (#11548 r44): an open pane showing this
  // artifact refreshes to the new version.
  | { kind: "artifact_updated"; artifactId: string }
  // A table artifact (#11727) to open beside the chat: a successful
  // create_table_artifact, or open_artifact answering open_as 'table'.
  | { kind: "open_table"; artifactId: string; title: string | null };

const UPDATE_TOOL_SUFFIX = "update_editor_document";
const OPEN_ARTIFACT_SUFFIX = "open_artifact";
const UPDATE_ARTIFACT_SUFFIX = "update_artifact";
// A rename (#12199) is reported like a content update: the pane handler
// refetches the row and applies its title, which is the change here.
const RENAME_ARTIFACT_SUFFIX = "rename_artifact";
const CREATE_TABLE_SUFFIX = "create_table_artifact";
const UPDATE_TABLE_SUFFIX = "update_table_artifact";
// Linking a table's names to records rewrites the table (a record column is
// filled) and saves a chat draft (#12430): the open pane refreshes as it does
// for an update.
const LINK_TABLE_SUFFIX = "link_table_records";

const toolSuffix = (toolName: string | undefined): string | null =>
  toolName ? (toolName.split("__").pop() ?? null) : null;

/** Tolerant field pull from either result serialization (JSON or Python
 * str(dict)) — same rationale as workspaceIdFromPreview. */
const fieldFromPreview = (
  outputPreview: string | undefined,
  field: string
): string | null => {
  if (!outputPreview) return null;
  const match = outputPreview.match(
    new RegExp(`["']${field}["']\\s*:\\s*["']([^"']+)["']`)
  );
  return match ? match[1] : null;
};

/** The created artifact id in a tool result's preview, both result
 * serializations tolerated — the table-segment resolver reads it (r78). */
export function artifactIdFromPreview(
  outputPreview: string | undefined
): string | null {
  return fieldFromPreview(outputPreview, "artifact_id");
}

/** Whether a tool step's name is the table-artifact create tool (r78). */
export function isCreateTableTool(toolName: string | undefined): boolean {
  return toolName ? toolName.split("__").pop() === CREATE_TABLE_SUFFIX : false;
}

/** Whether a tool step's name is the editor-document update tool (#12139):
 * the transcript cards an update-only turn's document from it. */
export function isUpdateEditorDocumentTool(
  toolName: string | undefined
): boolean {
  return toolSuffix(toolName) === UPDATE_TOOL_SUFFIX;
}

/** Whether a tool step's name is the editor-document tool. Matches on the
 * bare suffix too — migrated events and other MCP namespaces carry the same
 * trailing segment. */
export function isEditorDocumentTool(toolName: string | undefined): boolean {
  if (!toolName) return false;
  return (
    toolName === EDITOR_DOCUMENT_TOOL_NAME ||
    toolName.split("__").pop() === "create_editor_document"
  );
}

/**
 * Pull the created workspace id out of the tool result's bounded preview.
 * The preview is the stringified tool return — JSON from the SDK path or
 * Python `str(dict)` from the harness path — and both quote `workspace_id`
 * as a UUID, so a tolerant regex beats guessing which serialization
 * arrived. Null when the preview names no workspace (the tool errored, or
 * the preview was truncated before the id). The title is NOT parsed here:
 * the exact title is already on the tool call's input, and quoting rules
 * differ between the two serializations.
 */
export function workspaceIdFromPreview(
  outputPreview: string | undefined
): string | null {
  if (!outputPreview) return null;
  const id = outputPreview.match(
    /["']workspace_id["']\s*:\s*["']([0-9a-fA-F-]{36})["']/
  );
  return id ? id[1] : null;
}

/**
 * The stateful half of the contract, kept pure so it is testable: remembers
 * pending `create_editor_document` calls by tool_call_id (the title rides on
 * the call's input) and, when the matching non-error result lands, returns
 * the document to open. Everything else returns null.
 */
export class EditorDocumentTracker {
  private pending = new Map<
    string,
    {
      tool:
        | "create"
        | "update"
        | "open_artifact"
        | "update_artifact"
        | "create_table"
        | "update_table";
      title: string | null;
    }
  >();

  observe(update: ChatUpdate): EditorDocumentAction | null {
    if (update.type === "toolCall") {
      const suffix = toolSuffix(update.step.toolName);
      const tool = isEditorDocumentTool(update.step.toolName)
        ? ("create" as const)
        : isUpdateEditorDocumentTool(update.step.toolName)
          ? ("update" as const)
          : suffix === OPEN_ARTIFACT_SUFFIX
            ? ("open_artifact" as const)
            : suffix === UPDATE_ARTIFACT_SUFFIX ||
                suffix === RENAME_ARTIFACT_SUFFIX
              ? ("update_artifact" as const)
              : suffix === CREATE_TABLE_SUFFIX
                ? ("create_table" as const)
                : suffix === UPDATE_TABLE_SUFFIX || suffix === LINK_TABLE_SUFFIX
                  ? ("update_table" as const)
                  : null;
      if (tool) {
        this.pending.set(update.step.id, {
          tool,
          title:
            typeof update.step.input?.title === "string"
              ? update.step.input.title
              : null,
        });
      }
      return null;
    }
    if (update.type !== "toolResult") return null;
    const entry = this.pending.get(update.toolCallId);
    if (!entry) return null;
    this.pending.delete(update.toolCallId);
    if (update.error) return null;

    if (entry.tool === "create") {
      const workspaceId = workspaceIdFromPreview(update.outputPreview);
      return workspaceId
        ? {
            kind: "open",
            workspaceId,
            title: entry.title,
            artifactId: fieldFromPreview(update.outputPreview, "artifact_id"),
          }
        : null;
    }
    if (entry.tool === "update") {
      const workspaceId = workspaceIdFromPreview(update.outputPreview);
      return workspaceId ? { kind: "refresh", workspaceId } : null;
    }
    if (entry.tool === "update_artifact" || entry.tool === "update_table") {
      const artifactId = fieldFromPreview(update.outputPreview, "artifact_id");
      return artifactId ? { kind: "artifact_updated", artifactId } : null;
    }
    if (entry.tool === "create_table") {
      const artifactId = fieldFromPreview(update.outputPreview, "artifact_id");
      return artifactId
        ? {
            kind: "open_table",
            artifactId,
            title:
              entry.title ?? fieldFromPreview(update.outputPreview, "title"),
          }
        : null;
    }
    // open_artifact: route on the result's own open_as.
    const openAs = fieldFromPreview(update.outputPreview, "open_as");
    const title = fieldFromPreview(update.outputPreview, "title");
    if (openAs === "editor_document") {
      const workspaceId = workspaceIdFromPreview(update.outputPreview);
      return workspaceId
        ? {
            kind: "open",
            workspaceId,
            title,
            artifactId: fieldFromPreview(update.outputPreview, "artifact_id"),
          }
        : null;
    }
    if (openAs === "table") {
      const artifactId = fieldFromPreview(update.outputPreview, "artifact_id");
      return artifactId ? { kind: "open_table", artifactId, title } : null;
    }
    if (openAs === "file") {
      const artifactId = fieldFromPreview(update.outputPreview, "artifact_id");
      return artifactId
        ? {
            kind: "open_file",
            artifactId,
            title,
            filename: fieldFromPreview(update.outputPreview, "filename"),
          }
        : null;
    }
    return null;
  }

  /** Drop pending state when the surface switches conversations. */
  reset(): void {
    this.pending.clear();
  }
}
